/**
 * operatingCostService (Jose 2026-10-09) — the RECURRING software/infrastructure costs SLIK pays to run
 * (Firebase, Twilio, Deepgram, Anthropic/Claude, Apple, domain, Stripe fees…). This is the "list of all
 * the services this POS is paying" Jose asked for — NOT the barbershop services. Separate from the
 * one-off `expenses` ledger because these recur monthly; they roll into the P&L alongside shop expenses
 * + live Stripe fees. Seeded from SLIK-INFRASTRUCTURE-COST.md; all amounts are editable estimates.
 * Owner/manager only (rules). Collection: `operatingCosts`.
 */
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface OperatingCost {
  id: string;
  name: string;
  category: string;        // usually 'Software / Subscriptions' or 'Merchant / Bank Fees'
  monthlyCost: number;     // estimated $/mo (0 for purely usage-based, filled from live data elsewhere)
  usageBased?: boolean;    // true = scales with use (Stripe %, Twilio texts, Deepgram/Claude usage)
  note?: string;
  order?: number;
}

const COLL = 'operatingCosts';

// Seeded from the real stack (SLIK-INFRASTRUCTURE-COST.md). Rough monthly estimates — Jose edits to actuals.
const DEFAULTS: Array<Omit<OperatingCost, 'id'>> = [
  { name: 'Firebase (Blaze)',     category: 'Software / Subscriptions', monthlyCost: 15,   note: 'Database, hosting, functions, image storage', order: 1 },
  { name: 'Twilio',               category: 'Software / Subscriptions', monthlyCost: 15,   usageBased: true, note: 'SMS + toll-free 855-SLIK-602 + A2P', order: 2 },
  { name: 'Deepgram',             category: 'Software / Subscriptions', monthlyCost: 10,   usageBased: true, note: 'Call transcription (AI receptionist)', order: 3 },
  { name: 'Anthropic (Claude)',   category: 'Software / Subscriptions', monthlyCost: 20,   usageBased: true, note: 'AI', order: 4 },
  { name: 'Apple Developer',      category: 'Software / Subscriptions', monthlyCost: 8.25, note: '$99/yr — ship the iOS app', order: 5 },
  { name: 'Google Play',          category: 'Software / Subscriptions', monthlyCost: 0,    note: '$25 one-time (already paid)', order: 6 },
  { name: 'Domain',               category: 'Software / Subscriptions', monthlyCost: 2,    note: 'slikbarberlounge.com', order: 7 },
  { name: 'SendGrid (email)',     category: 'Software / Subscriptions', monthlyCost: 0,    note: 'Free tier', order: 8 },
  { name: 'Stripe processing',    category: 'Merchant / Bank Fees',     monthlyCost: 0,    usageBased: true, note: '~2.7–2.9% of card volume — live figure in Settings → Payments', order: 9 },
];

export const operatingCostService = {
  async list(): Promise<OperatingCost[]> {
    try {
      const snap = await getDocs(query(collection(db, COLL), orderBy('order', 'asc')));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as OperatingCost[];
    } catch { return []; }
  },
  async add(c: Omit<OperatingCost, 'id'>): Promise<string> {
    const clean: Record<string, any> = { name: c.name, category: c.category, monthlyCost: Number(c.monthlyCost) || 0, order: c.order ?? 999 };
    if (c.usageBased) clean.usageBased = true;
    if (c.note) clean.note = c.note;
    const ref = await addDoc(collection(db, COLL), clean);
    return ref.id;
  },
  async update(id: string, patch: Partial<OperatingCost>): Promise<void> {
    await updateDoc(doc(db, COLL, id), patch as any);
  },
  async remove(id: string): Promise<void> {
    await deleteDoc(doc(db, COLL, id));
  },
  /** First-run: populate the default stack if the list is empty. Returns the (now-seeded) list. */
  async seedDefaultsIfEmpty(): Promise<OperatingCost[]> {
    const existing = await this.list();
    if (existing.length) return existing;
    await Promise.all(DEFAULTS.map((d) => this.add(d)));
    return this.list();
  },
};

export default operatingCostService;
